/**
 * Cloudflare Analytics Service
 * Handles Cloudflare Web Analytics and Zaraz integration
 */

type AnalyticsProperties = Record<
  string,
  string | number | boolean | undefined
>;

interface CloudflareBeacon {
  (event: string, properties?: AnalyticsProperties): void;
}

declare global {
  interface Window {
    cloudflareInsights?: {
      track: CloudflareBeacon;
    };
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

// Environment detection helpers (same pattern as other services)
const getImportMetaEnv = (): Record<string, string> | undefined => {
  try {
    return (0, eval)('import.meta.env');
  } catch {
    return undefined;
  }
};

const getEnv = (key: string): string | undefined => {
  const meta = getImportMetaEnv();
  if (meta && key in meta) {
    return meta[key];
  }
  if (typeof process !== 'undefined' && process.env) {
    return (process.env as Record<string, string | undefined>)[key];
  }
  return undefined;
};

class CloudflareAnalyticsService {
  private token: string;
  private isInitialized: boolean = false;
  private isDevelopment: boolean;

  constructor() {
    this.token = getEnv('VITE_CLOUDFLARE_ANALYTICS_TOKEN') || '';
    this.isDevelopment = this.checkDevelopmentEnvironment();
    this.init();
  }

  private checkDevelopmentEnvironment(): boolean {
    if (typeof window === 'undefined') {
      return false; // SSR
    }

    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.includes('localhost') ||
      getEnv('NODE_ENV') === 'development' ||
      getEnv('VITE_MODE') === 'development'
    );
  }

  private init(): void {
    if (typeof window === 'undefined') {
      // SSR environment, silently skip
      return;
    }

    // Completely disable analytics in development
    if (this.isDevelopment) {
      console.log(
        '🔧 Cloudflare Analytics: Disabled in development environment',
      );
      return;
    }

    if (!this.token) {
      return;
    }

    // Initialize Web Analytics if not already done
    if (!window.cloudflareInsights) {
      // The beacon script will set this up automatically
      this.isInitialized = true;
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, properties?: AnalyticsProperties): void {
    if (typeof window === 'undefined') {
      // SSR environment, silently skip
      return;
    }

    // Skip all tracking in development
    if (this.isDevelopment) {
      return;
    }

    if (!this.token) {
      return;
    }

    if (!this.isInitialized) {
      return;
    }

    try {
      // For Cloudflare Web Analytics, custom events can be tracked via data attributes
      // or by using the beacon directly if available
      if (window.cloudflareInsights?.track) {
        window.cloudflareInsights.track(eventName, properties);
      } else {
        // Fallback: Create a custom beacon call
        this.sendCustomEvent(eventName, properties);
      }
    } catch (error) {
      console.error('Error tracking Cloudflare Analytics event:', error);
    }
  }

  /**
   * Track page view (usually handled automatically by the beacon)
   */
  trackPageView(path?: string): void {
    const pagePath = path || window.location.pathname;
    this.trackEvent('pageview', { path: pagePath });
  }

  /**
   * Track user interaction
   */
  trackInteraction(
    element: string,
    action: string,
    value?: string | number,
  ): void {
    this.trackEvent('interaction', {
      element,
      action,
      value,
      timestamp: Date.now(),
    });
  }

  /**
   * Track space-related events specific to RocketCityNerd
   */
  trackSpaceEvent(
    eventType:
      | 'launch_view'
      | 'news_read'
      | 'event_signup'
      | 'tool_use'
      | 'observation_upload',
    data?: AnalyticsProperties,
  ): void {
    this.trackEvent(`space_${eventType}`, {
      ...data,
      category: 'space',
      timestamp: Date.now(),
    });
  }

  /**
   * Track blog interactions
   */
  trackBlogEvent(
    action: 'read' | 'share' | 'comment',
    postId?: string,
    postTitle?: string,
  ): void {
    this.trackEvent('blog_interaction', {
      action,
      postId,
      postTitle,
      category: 'blog',
      timestamp: Date.now(),
    });
  }

  /**
   * Track tool usage from the engineering toolkit
   */
  trackToolUsage(toolName: string, category?: string): void {
    this.trackEvent('tool_usage', {
      toolName,
      category: category || 'engineering',
      timestamp: Date.now(),
    });
  }

  /**
   * Send custom event via fetch if beacon not available
   */
  private sendCustomEvent(
    eventName: string,
    properties?: AnalyticsProperties,
  ): void {
    // This is a fallback method - Cloudflare Web Analytics primarily uses the beacon
    console.log('Cloudflare Analytics Event:', eventName, properties);

    // You could implement a custom endpoint here if needed
    // For now, we'll just log it as Cloudflare Web Analytics handles most tracking automatically
  }

  /**
   * Initialize Zaraz if available
   */
  initZaraz(): void {
    if (typeof window === 'undefined') return;

    // Zaraz is typically loaded automatically by Cloudflare
    // This method can be used to trigger custom Zaraz events
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'zaraz_init',
        site: 'rocketcitynerd',
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Send event to Zaraz
   */
  sendZarazEvent(eventName: string, data?: AnalyticsProperties): void {
    if (typeof window === 'undefined') return;

    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...data,
        timestamp: Date.now(),
      });
    }
  }
}

// Create singleton instance
const cloudflareAnalytics = new CloudflareAnalyticsService();

export default cloudflareAnalytics;

// Named exports for convenience
export const trackEvent =
  cloudflareAnalytics.trackEvent.bind(cloudflareAnalytics);
export const trackPageView =
  cloudflareAnalytics.trackPageView.bind(cloudflareAnalytics);
export const trackInteraction =
  cloudflareAnalytics.trackInteraction.bind(cloudflareAnalytics);
export const trackSpaceEvent =
  cloudflareAnalytics.trackSpaceEvent.bind(cloudflareAnalytics);
export const trackBlogEvent =
  cloudflareAnalytics.trackBlogEvent.bind(cloudflareAnalytics);
export const trackToolUsage =
  cloudflareAnalytics.trackToolUsage.bind(cloudflareAnalytics);
